import { FC, ReactNode } from "react";
import { AlertTriangle, Check } from "lucide-react";
import { BuildingFuture, BuildingOccupancy, CopropertyEligibilityDto, CopropertyEligibilityStatus, BuildingStanding } from "api/coproperty";

/** Contrôles et helpers partagés par les formulaires de l'étape copropriété (MRI). */

export interface EligibilityVerdict {
  status: CopropertyEligibilityStatus;
  reasons: string[];
}

/** Verdict tel que renvoyé par l'API copropriété (réponse d'enregistrement ou synthèse). */
export const toVerdict = (dto: CopropertyEligibilityDto | null | undefined): EligibilityVerdict | null =>
  dto ? { status: dto.status, reasons: dto.reasons ?? [] } : null;

/** Standing de l'immeuble : fixe la valeur de reconstruction au m² du SMP (barème porté par le back). */
export const BUILDING_STANDING_OPTIONS: { value: BuildingStanding; label: string }[] = [
  { value: 1, label: "Standard" },
  { value: 2, label: "Bon standing" },
  { value: 3, label: "Luxe" },
];

/** Standing par défaut d'une copropriété, alignée sur le domaine (Standard). */
export const DEFAULT_BUILDING_STANDING: BuildingStanding = 1;

export const buildingStandingLabel = (value: BuildingStanding | null | undefined): string | null =>
  BUILDING_STANDING_OPTIONS.find((o) => o.value === value)?.label ?? null;

export const OCCUPANCY_OPTIONS: { value: BuildingOccupancy; label: string }[] = [
  { value: 0, label: "Plus de 50 %" },
  { value: 1, label: "Moins de 50 %" },
  { value: 2, label: "Totalement vide" },
];

export const FUTURE_OPTIONS: { value: BuildingFuture; label: string }[] = [
  { value: 0, label: "Aucun des deux" },
  { value: 1, label: "Réhabilitation" },
  { value: 2, label: "Démolition" },
];

/** Couleurs communes des contrôles à sélection (pills Oui/Non, puces de nature). */
const choiceToneClass = (selected: boolean) =>
  selected
    ? "border-primary bg-primary/10 text-primary"
    : "border-gray-300 text-gray-600 hover:bg-gray-50";

/** Choix binaire oui/non sur bool? (null = non répondu). */
export const BooleanChoice: FC<{
  label: string;
  value: boolean | null | undefined;
  labels?: [string, string];
  hint?: string;
  onChange: (value: boolean) => void;
}> = ({ label, value, labels = ["Oui", "Non"], hint, onChange }) => (
  <div>
    <span className="mb-1 block text-sm font-medium text-gray-700">{label}</span>
    <div className="flex gap-2">
      {[true, false].map((choice, index) => (
        <button
          key={String(choice)}
          type="button"
          onClick={() => onChange(choice)}
          className={`rounded-lg border px-4 py-2 text-sm font-medium transition-colors ${choiceToneClass(
            value === choice
          )}`}
        >
          {labels[index]}
        </button>
      ))}
    </div>
    {hint && <p className="mt-1 text-xs text-gray-500">{hint}</p>}
  </div>
);

/** Variante compacte du choix binaire : libellé à gauche, pills Oui/Non alignées à droite. */
export const CompactBooleanRow: FC<{
  label: string;
  value: boolean | null | undefined;
  hint?: string;
  /** Réponse nécessaire au verdict d'éligibilité : astérisque comme sur les champs de saisie. */
  required?: boolean;
  onChange: (value: boolean) => void;
}> = ({ label, value, hint, required = false, onChange }) => (
  <div className="flex flex-col gap-1">
    <div className="flex items-center justify-between gap-3">
      <span className="text-sm text-gray-700">
        {label}
        {required && <span className="ml-0.5 text-red-500">*</span>}
      </span>
      <div className="flex flex-none gap-1.5">
        {[true, false].map((choice) => (
          <button
            key={String(choice)}
            type="button"
            onClick={() => onChange(choice)}
            className={`rounded-lg border px-3.5 py-1.5 text-[13px] transition-colors ${choiceToneClass(
              value === choice
            )}`}
          >
            {choice ? "Oui" : "Non"}
          </button>
        ))}
      </div>
    </div>
    {hint && <p className="text-xs text-gray-500">{hint}</p>}
  </div>
);

export interface ChoiceCardOption<T extends string> {
  value: T;
  label: string;
  /** Ligne d'explication sous le titre : ce que la réponse implique pour la suite. */
  description?: string;
  icon?: ReactNode;
}

interface ChoiceCardsProps<T extends string> {
  label: string;
  options: ChoiceCardOption<T>[];
  value: T | null;
  required?: boolean;
  onChange: (value: T) => void;
}

/** Choix unique parmi plusieurs réponses, en cartes côte à côte (icône, titre, explication). */
export function ChoiceCards<T extends string>({ label, options, value, required = false, onChange }: ChoiceCardsProps<T>) {
  return (
    <div className="flex flex-col gap-2">
      <span className="text-sm font-medium text-gray-700">
        {label}
        {required && <span className="ml-0.5 text-red-500">*</span>}
      </span>
      <div role="radiogroup" aria-label={label} className="grid grid-cols-1 gap-3 md:grid-cols-3">
        {options.map((option) => {
          const selected = value === option.value;
          return (
            <button
              key={option.value}
              type="button"
              role="radio"
              aria-checked={selected}
              onClick={() => onChange(option.value)}
              className={`group relative flex w-full flex-col gap-2 rounded-xl border-2 p-4 text-left transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 ${
                selected
                  ? "border-primary bg-primary/10"
                  : "border-gray-200 bg-white hover:border-primary/50 hover:shadow-sm"
              }`}
            >
              <span
                className={`absolute right-3 top-3 flex h-5 w-5 items-center justify-center rounded-full border-2 transition-all ${
                  selected ? "border-primary bg-primary" : "border-gray-300 group-hover:border-primary/50"
                }`}
              >
                {selected && <Check className="h-3 w-3 text-white" />}
              </span>
              {option.icon && (
                <span
                  className={`flex h-10 w-10 flex-none items-center justify-center rounded-xl transition-colors ${
                    selected ? "bg-primary text-white" : "bg-gray-100 text-gray-500 group-hover:bg-primary/10 group-hover:text-primary"
                  }`}
                >
                  {option.icon}
                </span>
              )}
              <span className={`pr-6 text-sm font-semibold ${selected ? "text-primary" : "text-gray-800"}`}>
                {option.label}
              </span>
              {option.description && <span className="text-[13px] text-gray-500">{option.description}</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** Puce de nature (habitation, bureaux…) : coche visible quand la nature est présente. */
export const NatureChip: FC<{
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}> = ({ label, checked, onChange }) => (
  <button
    type="button"
    onClick={() => onChange(!checked)}
    className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-[13px] transition-colors ${choiceToneClass(
      checked
    )}`}
  >
    {checked && <Check className="h-3.5 w-3.5" />}
    {label}
  </button>
);

/** Pastille du verdict d'éligibilité, pour l'en-tête de la fiche compacte. */
export const EligibilityChip: FC<{ verdict: EligibilityVerdict | null }> = ({ verdict }) => {
  if (!verdict) {
    return (
      <span className="inline-flex flex-none items-center rounded-full border border-gray-200 bg-gray-50 px-3 py-1.5 text-[13px] text-gray-500">
        Éligibilité à vérifier
      </span>
    );
  }

  if (verdict.status === CopropertyEligibilityStatus.Eligible) {
    return (
      <span className="inline-flex flex-none items-center gap-1.5 rounded-full border border-green-200 bg-green-50 px-3 py-1.5 text-[13px] font-semibold text-green-700">
        <Check className="h-3.5 w-3.5" />
        Éligible en l'état
      </span>
    );
  }

  if (verdict.status === CopropertyEligibilityStatus.NonEligible) {
    return (
      <span className="inline-flex flex-none items-center gap-1.5 rounded-full border border-red-200 bg-red-50 px-3 py-1.5 text-[13px] font-semibold text-red-700">
        <AlertTriangle className="h-3.5 w-3.5" />
        Non éligible
      </span>
    );
  }

  return (
    <span className="inline-flex flex-none items-center rounded-full border border-amber-200 bg-amber-50 px-3 py-1.5 text-[13px] font-semibold text-amber-700">
      À compléter
    </span>
  );
};

/** Parse une saisie décimale FR/EN ("12,5" ou "12.5") ; null si vide. */
export const parseDecimal = (raw: string): number | null =>
  raw.trim() ? Number(raw.replace(",", ".")) : null;

/** Bandeau de verdict (éligible / à compléter / non éligible) avec ses raisons. */
export const EligibilityVerdictBanner: FC<{ verdict: EligibilityVerdict }> = ({ verdict }) => {
  const isEligible = verdict.status === CopropertyEligibilityStatus.Eligible;
  const isNonEligible = verdict.status === CopropertyEligibilityStatus.NonEligible;

  return (
    <div
      className={`rounded-xl border p-4 text-sm ${
        isEligible
          ? "border-green-200 bg-green-50 text-green-700"
          : isNonEligible
            ? "border-red-200 bg-red-50 text-red-700"
            : "border-amber-200 bg-amber-50 text-amber-700"
      }`}
    >
      <p className="font-semibold">
        {isEligible && "Copropriété éligible"}
        {isNonEligible && "Copropriété non éligible"}
        {!isEligible && !isNonEligible && "Éligibilité à compléter"}
      </p>
      {verdict.reasons.map((reason) => (
        <p key={reason}>{reason}</p>
      ))}
    </div>
  );
};
